import { LanguageURL } from '../../../../../core/src'
import { IRESTClient } from '../../base/api/i-rest-client'
import { SitemapRouteChangeFrequency } from './change-frequency.enum'
import { ISitemap } from './i-sitemap'
import { ISitemapRoute } from './i-sitemap-route'

export class SitemapGenerator {
  static async getSitemap(env: NodeJS.ProcessEnv, restClient: IRESTClient, vacancyIdsUrl: string): Promise<ISitemap> {
    const exclude = SitemapGenerator.getExcludedRoutes()

    const routes = await SitemapGenerator.routes(restClient, vacancyIdsUrl)
    const map: ISitemap = { hostname: env.WEB_URL!, exclude, routes }
    return map
  }

  /**
   * Amount of milliseconds to wait until the sitemap is refreshed.
   * @returns
   */
  static getCacheTime(): number {
    const hoursPerDay = 24
    const minsPerHour = 60
    const secsPerMinute = 60
    const milsPerSecond = 1000
    return milsPerSecond * secsPerMinute * minsPerHour * hoursPerDay
  }

  /**
   * XML schema.
   * @returns
   */
  static getXmlns(): string {
    return 'http://www.sitemaps.org/schemas/sitemap/0.9'
  }

  static async routes(restClient: IRESTClient, vacancyIdsUrl: string): Promise<string[] | ISitemapRoute[]> {
    const routes: ISitemapRoute[] = [
      { url: '/', changefreq: SitemapRouteChangeFrequency.daily, priority: 1 },
      { url: '/about', changefreq: SitemapRouteChangeFrequency.yearly, priority: 0.5 },
      { url: '/terms-and-conditions', changefreq: SitemapRouteChangeFrequency.yearly, priority: 0.5 },
      { url: '/terms-and-conditions/jp', changefreq: SitemapRouteChangeFrequency.yearly, priority: 0.5 },
      { url: '/jp/agent/privacy-policy', changefreq: SitemapRouteChangeFrequency.yearly, priority: 0.5 },
    ]
    const languages = Object.values(LanguageURL)
    const nonEnLangs = languages.filter((e) => e !== LanguageURL.EN)
    const ids = await SitemapGenerator.getVacancyIds(restClient, vacancyIdsUrl)
    ids.map((e) => {
      const route = SitemapGenerator.getVacancyDetailsRoute(e)
      routes.push(route)
      nonEnLangs.forEach((lang) => {
        const newLangRoute: string | ISitemapRoute = { ...route, url: `/${lang}${route.url}` }
        routes.push(newLangRoute)
      })
    })
    return routes
  }

  /**
   * Retrieves public vacancy ids.
   * @param restClient
   * @param vacancyIdsUrl
   * @returns
   */
  static async getVacancyIds(restClient: IRESTClient, vacancyIdsUrl: string): Promise<number[]> {
    const response = await restClient.get(vacancyIdsUrl)
    const ids: number[] = response.data
    return ids
  }

  /**
   * Creates a vacancy URL from a given id
   * @param id
   * @returns
   */
  static getVacancyDetailsRoute(id: number): ISitemapRoute {
    const route: ISitemapRoute = { url: `/job/${id}`, changefreq: SitemapRouteChangeFrequency.monthly, priority: 0.5 }
    return route
  }

  /**
   * Routes to exclude from our sitemap.xml file
   * @returns
   */
  static getExcludedRoutes(): string[] {
    return [
      '**/a/**',
      '**/account/',
      '**/account/**',
      '**/application/**',
      '**/auth/**',
      '**/job/_id/preview/**',
      '**/job/_id/quick-apply/',
      '**/job/_id/application_success/',
      '**/job/_id/application_verify/',
      '**/job/_id/translation-not-available/',
      '/profile',
      '/profile/**',
      // Do we still need the following routes below?
      '/dashboard',
      '/application',
      '/application/**',
      '/resume',
      '/resume/**',
      '/security',
      '/security/**',
      '/bot/**',
    ]
  }
}
